import { api } from './api';

interface FetchMarketingProductHighlightsParams {
  q?: string;
  category_id?: string | string[];
  category_type?: string;
  commission_min?: number | string;
  commission_max?: number | string;
  price_min?: number | string;
  price_max?: number | string;
}

export type MarketplaceCommission = {
  type?: string;
  percent?: number | null;
  value?: number | null;
  value_formatted?: string;
};

export type MarketplaceOffer = {
  id: string;
  name: string;
  price: number;
  currency: string;
  price_formatted: string;
  commission: MarketplaceCommission;
};

export type MarketplaceProductListItem = {
  id: number;
  uuid: string;
  name: string;
  description: string;
  images: Array<{ url: string }>;
  offers_grouped: Record<string, MarketplaceOffer[]>;
  commission_formatted?: number;
};

export type MarketplaceHighlightsResponse = {
  top_selling: MarketplaceProductListItem[];
  trending: MarketplaceProductListItem[];
  recent: MarketplaceProductListItem[];
};

export type MarketplaceProductLink = {
  id: string;
  name: string;
  url: string;
};

export type MarketplaceCheckout = {
  id: string;
  name: string;
  url: string;
  price_formatted: string;
};

export type MarketplaceProductDetails = {
  product: {
    uuid: string;
    name: string;
    description?: string;
    images?: Array<{ url: string }>;
    phone_prefix?: string;
    category?: { type?: string; name?: string };
    email_support?: string;
    whatsapp_support?: string;
    product_affiliate_program?: {
      attribution?: string;
      cookie_duration?: number | string;
      contact_email?: string;
      page_url?: string;
      description?: string;
    };
  };
  offers_grouped: Record<string, MarketplaceOffer[]>;
  seller_name?: string;
  relationship?: string | null;
  invitation_status?: string | null;
  checkouts?: MarketplaceCheckout[];
  links?: MarketplaceProductLink[];
};

async function fetchMarketingProductHighlights(
  params?: FetchMarketingProductHighlightsParams
): Promise<MarketplaceHighlightsResponse> {
  const {
    q,
    category_id,
    category_type,
    commission_min,
    commission_max,
    price_min,
    price_max,
  } = params || {};
  const urlParams = new URLSearchParams();

  if (category_id) {
    const categoryIdArray = Array.isArray(category_id)
      ? category_id
      : [category_id];
    categoryIdArray.forEach((id) => urlParams.append('category_id', String(id)));
  }
  if (category_type) urlParams.append('category_type', category_type);

  if (price_min !== undefined && price_min !== null && price_min !== '') {
    urlParams.append('price_min', String(price_min));
  }
  if (price_max !== undefined && price_max !== null && price_max !== '') {
    urlParams.append('price_max', String(price_max));
  }

  if (
    commission_min !== undefined &&
    commission_min !== null &&
    commission_min !== ''
  ) {
    urlParams.append('commission_min', Number(commission_min).toFixed(2));
  }
  if (
    commission_max !== undefined &&
    commission_max !== null &&
    commission_max !== ''
  ) {
    urlParams.append('commission_max', Number(commission_max).toFixed(2));
  }

  if (q && q.length > 1) urlParams.append('q', q);

  const response = await api.get(
    `/v1/marketplace/products/highlights?${urlParams.toString()}`
  );

  return response.data as MarketplaceHighlightsResponse;
}

async function productHighlightsDetails(
  id: string,
): Promise<MarketplaceProductDetails> {
  const response = await api.get(`/v1/marketplace/products/${id}`);

  return response.data as MarketplaceProductDetails;
}

export const MarketingService = {
  fetchMarketingProductHighlights,
  productHighlightsDetails,
};
